import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  TemplateRef,
  untracked,
  viewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  createContextLogger,
  injectShowTextTuiAlert,
  injectShowTextTuiPrompt
} from '@konnektu/helpers';
import { MetastoreService, TableFullDto } from '@konnektu/metastore';
import { injectTenantNavigate } from '@konnektu/multitenant';
import { TopBarService } from '@konnektu/sidebar-layout';
import {
  TuiButton,
  TuiDataList,
  TuiDropdown,
  TuiLabel,
  TuiTextfield
} from '@taiga-ui/core';
import {
  TuiButtonLoading,
  TuiCheckbox,
  TuiChevron,
  TuiDataListWrapper,
  TuiSelect
} from '@taiga-ui/kit';
import { filter, map, Observable, of, switchMap, tap } from 'rxjs';
import {
  SectionConfigMetadata,
  SectionEditorMetadata,
  TopAction,
  TopBarMetadata
} from '../config-transpiler/models';
import { validatePayload } from '../config-transpiler/payload-construction';
import { CardSelectorComponent } from '../shared/card-selector/card-selector.component';
import {
  ColumnConfiguration,
  ColumnConfiguratorComponent
} from '../shared/column-configurator/column-configurator.component';
import { EntitySelectorComponent } from '../shared/entity-selector/entity-selector.component';
import { PageLabelInputComponent } from '../shared/page-label-input/page-label-input.component';
import { PagePathInputComponent } from '../shared/page-path-input/page-path-input.component';
import {
  injectSaveConflictGuard,
  stampGeneratedPayloadHash
} from '../shared/payload-drift';
import { PlatformViewSummaryComponent } from '../shared/platform-view-summary/platform-view-summary.component';
import { isRawSectionView, parseEditorMetadata } from '../shared/raw-view';
import { injectSaveAsCopy } from '../shared/save-as-copy-dialog';
import { injectTopActionsDialog } from '../shared/top-actions-editor/inject-top-actions-dialog';
import {
  buildSectionEditorMetadata,
  hasSectionEditorChanges,
  reconcileCreateAction,
  SectionEditorState,
  sectionParamsFromMetadata
} from './section-editor-metadata';
import { generateSectionPayload } from './section-payload.generator';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
@Component({
  selector: 'knk-section-editor',
  templateUrl: './section-editor.component.html',
  styleUrls: ['./section-editor.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    TuiButton,
    TuiTextfield,
    TuiLabel,
    TuiCheckbox,
    TuiDropdown,
    TuiDataList,
    TuiSelect,
    TuiChevron,
    TuiDataListWrapper,
    EntitySelectorComponent,
    PageLabelInputComponent,
    PagePathInputComponent,
    ColumnConfiguratorComponent,
    CardSelectorComponent,
    TuiButtonLoading,
    PlatformViewSummaryComponent,
    TranslateModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SectionEditorComponent {
  private readonly logger = createContextLogger('SectionEditorComponent');
  private readonly translate = inject(TranslateService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly topBarService = inject(TopBarService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly metastore = inject(MetastoreService);
  private readonly tenantNavigate = injectTenantNavigate();
  private readonly showTextAlert = injectShowTextTuiAlert();
  private readonly showPrompt = injectShowTextTuiPrompt();
  private readonly openTopActionsDialog = injectTopActionsDialog();
  private readonly saveAsCopyFlow = injectSaveAsCopy();
  private readonly saveConflictGuard = injectSaveConflictGuard();

  protected readonly actionTemplate =
    viewChild.required<TemplateRef<void>>('actionTemplate');

  protected readonly resolvedSection = toSignal(
    this.activatedRoute.data.pipe(
      map(
        (data) =>
          data['dynamicSection'] as
            | {
                Id: string;
                Path: string;
                Payload: string;
                Metadata: string;
                ModifiedOn?: string | null;
              }
            | undefined
      )
    ),
    { requireSync: true }
  );

  private readonly loadedModifiedOn = signal<string | null>(null);

  protected readonly sectionMetadata = computed(
    () =>
      parseEditorMetadata<SectionEditorMetadata>(
        this.resolvedSection()?.Metadata
      ) ?? ({} as SectionEditorMetadata)
  );

  private readonly entityReset = signal(false);

  protected readonly isPlatformView = computed(() => {
    const section = this.resolvedSection();
    return !!section && isRawSectionView(section.Metadata);
  });

  protected readonly label = signal('');
  protected readonly path = signal('');
  protected readonly entity = signal<TableFullDto | null>(null);
  protected readonly columns = signal<ColumnConfiguration[]>([]);
  protected readonly linkedCardPath = signal<string | null>(null);
  protected readonly allowCreate = signal(false);
  protected readonly allowDelete = signal(false);
  protected readonly topActions = signal<TopAction[]>([]);
  protected readonly topBar = signal<TopBarMetadata | undefined>(undefined);
  protected readonly hasTopBarCode = computed(
    () => this.topBar()?.kind === 'raw'
  );
  protected readonly config = signal<SectionConfigMetadata | undefined>(
    undefined
  );
  protected readonly hasConfigCode = computed(
    () => this.config()?.kind === 'raw'
  );
  protected readonly tableSize = signal<'s' | 'm' | 'l'>('m');
  protected readonly showManualFilter = signal(false);
  protected readonly showFilterPresets = signal(false);
  protected readonly importLegacyFolders = signal(false);

  protected readonly isSaving = signal(false);

  protected readonly tableSizeOptions = ['s', 'm', 'l'] as const;

  private readonly tableSizeKeys: Record<'s' | 'm' | 'l', string> = {
    s: 'dynamicEditor.sizeS',
    m: 'dynamicEditor.sizeM',
    l: 'dynamicEditor.sizeL'
  };

  protected readonly tableSizeLabel = computed(() =>
    this.translate.instant(this.tableSizeKeys[this.tableSize()])
  );

  protected readonly tableSizeLabels = this.tableSizeOptions.map((size) =>
    this.translate.instant(this.tableSizeKeys[size])
  );

  protected updateTableSizeFromLabel(label: string) {
    const size = this.tableSizeOptions.find(
      (option) => this.translate.instant(this.tableSizeKeys[option]) === label
    );
    this.tableSize.set(size ?? 'm');
  }

  private readonly loadedState = signal<SectionEditorState>({
    label: '',
    path: '',
    entityName: '',
    columns: [],
    linkedCardPath: null,
    allowCreate: false,
    allowDelete: false,
    topActions: [],
    tableSize: 'm',
    showManualFilter: false,
    showFilterPresets: false,
    importLegacyFolders: false,
    topBar: undefined,
    config: undefined
  });

  protected readonly hasUnsavedChanges = computed(() => {
    if (this.isPlatformView()) {
      return false;
    }
    return hasSectionEditorChanges(
      {
        label: this.label(),
        path: this.path(),
        entityName: this.entity()?.name ?? '',
        columns: this.columns(),
        linkedCardPath: this.linkedCardPath(),
        allowCreate: this.allowCreate(),
        allowDelete: this.allowDelete(),
        topActions: this.topActions(),
        topBar: this.topBar(),
        config: this.config(),
        tableSize: this.tableSize(),
        showManualFilter: this.showManualFilter(),
        showFilterPresets: this.showFilterPresets(),
        importLegacyFolders: this.importLegacyFolders()
      },
      this.loadedState()
    );
  });

  private readonly syncCreateActionEffect = effect(() => {
    const allowCreate = this.allowCreate();
    const linkedCardPath = this.linkedCardPath();
    untracked(() => {
      const current = this.topActions();
      const next = reconcileCreateAction(current, allowCreate, linkedCardPath);
      if (next !== current) {
        this.topActions.set(next);
      }
    });
  });

  private readonly onSectionChangeEffect = effect((onCleanup) => {
    const section = this.resolvedSection();
    const metadata = this.sectionMetadata();
    this.entityReset.set(false);
    if (section) {
      const loadedTopActions = reconcileCreateAction(
        metadata.topActions ?? [],
        metadata.allowCreate ?? false,
        metadata.linkedCardPath ?? null
      );
      const loadedTopBar =
        metadata.topBar?.kind === 'raw' ? metadata.topBar : undefined;

      this.label.set(metadata.label ?? '');
      this.path.set(metadata.path ?? section.Path ?? '');
      this.columns.set(metadata.columns || []);
      this.allowCreate.set(metadata.allowCreate ?? false);
      this.allowDelete.set(metadata.allowDelete ?? false);
      this.linkedCardPath.set(metadata.linkedCardPath ?? null);
      this.topActions.set(loadedTopActions);
      this.topBar.set(loadedTopBar);
      this.config.set(metadata.config);
      this.tableSize.set(metadata.tableSize ?? 'm');
      this.showManualFilter.set(metadata.showManualFilter ?? false);
      this.showFilterPresets.set(metadata.showFilterPresets ?? false);
      this.importLegacyFolders.set(metadata.importLegacyFolders ?? false);
      this.loadedState.set({
        label: metadata.label ?? '',
        path: metadata.path ?? section.Path ?? '',
        entityName: metadata.entityName ?? '',
        columns: metadata.columns ?? [],
        linkedCardPath: metadata.linkedCardPath ?? null,
        allowCreate: metadata.allowCreate ?? false,
        allowDelete: metadata.allowDelete ?? false,
        topActions: loadedTopActions,
        topBar: loadedTopBar,
        config: metadata.config,
        tableSize: metadata.tableSize ?? 'm',
        showManualFilter: metadata.showManualFilter ?? false,
        showFilterPresets: metadata.showFilterPresets ?? false,
        importLegacyFolders: metadata.importLegacyFolders ?? false
      });
      this.loadedModifiedOn.set(section.ModifiedOn ?? null);
      if (metadata.entityName) {
        const subscription = this.metastore
          .tableMetadata(metadata.entityName, {
            ui: { section: 'CardEditor' }
          })
          .subscribe((dto) => {
            if (dto) {
              this.entity.set(dto);
            }
          });
        onCleanup(() => subscription.unsubscribe());
      } else {
        this.entity.set(null);
      }
      this.topBarService.configureTopBar(
        {
          actionTemplate: this.actionTemplate(),
          headerLabel: 'dynamicEditor.sectionEditTitle'
        },
        this.destroyRef
      );
    } else {
      this.topBarService.configureTopBar(
        {
          actionTemplate: this.actionTemplate(),
          headerLabel: 'dynamicEditor.sectionCreateTitle'
        },
        this.destroyRef
      );
    }
  });

  protected readonly confirmEntityChange = (): Observable<boolean> => {
    const hasConfig =
      this.columns().length > 0 ||
      this.linkedCardPath() !== null ||
      this.allowCreate() ||
      this.allowDelete() ||
      this.topBar() !== undefined ||
      this.config() !== undefined;

    if (!hasConfig) {
      return of(true);
    }

    return this.showPrompt(
      'dynamicEditor.changeEntitySectionConfirm',
      'dynamicEditor.change'
    ).pipe(
      tap((confirmed) => {
        if (confirmed) {
          this.columns.set([]);
          this.linkedCardPath.set(null);
          this.allowCreate.set(false);
          this.allowDelete.set(false);
          this.topActions.set([]);
          this.topBar.set(undefined);
          this.config.set(undefined);
          this.entityReset.set(true);
        }
      })
    );
  };

  protected removeTopBarCode() {
    this.topBar.set(undefined);
  }

  protected removeConfigCode() {
    this.showPrompt(
      'dynamicEditor.removeConfigCodeConfirm',
      'dynamicEditor.removeConfigCode'
    )
      .pipe(filter(Boolean))
      .subscribe(() => this.config.set(undefined));
  }

  protected openTopActions() {
    this.openTopActionsDialog({ topActions: this.topActions() })
      .pipe(filter(Boolean))
      .subscribe((result) => this.topActions.set(result));
  }

  private buildMetadata(
    entityName: string,
    path: string,
    label: string
  ): SectionEditorMetadata {
    const saved = this.sectionMetadata();
    return buildSectionEditorMetadata(
      {
        label,
        path,
        entityName,
        columns: this.columns(),
        linkedCardPath: this.linkedCardPath(),
        allowCreate: this.allowCreate(),
        allowDelete: this.allowDelete(),
        topActions: this.topActions(),
        topBar: this.topBar(),
        config: this.config(),
        tableSize: this.tableSize(),
        showManualFilter: this.showManualFilter(),
        showFilterPresets: this.showFilterPresets(),
        importLegacyFolders: this.importLegacyFolders()
      },
      this.entityReset()
        ? ({ origin: saved.origin } as SectionEditorMetadata)
        : saved
    );
  }

  private generatePayload(metadata: SectionEditorMetadata): string {
    return generateSectionPayload(sectionParamsFromMetadata(metadata)).payload;
  }

  saveChanges() {
    const label = this.label();
    const path = this.path();
    const entity = this.entity();
    const section = this.resolvedSection();

    if (this.isPlatformView()) {
      return;
    }
    if (!label || !path || !entity) {
      this.showTextAlert('dynamicEditor.fillRequiredFields', 'negative');
      return;
    }

    const baseMetadata = this.buildMetadata(entity.name, path, label);

    this.isSaving.set(true);

    let payload: string;
    try {
      payload = this.generatePayload(baseMetadata);
      const validation = validatePayload(payload);
      if (!validation.valid) {
        throw new Error(validation.error);
      }
    } catch (err) {
      this.isSaving.set(false);
      this.showTextAlert(
        this.translate.instant('dynamicEditor.generateViewErrorDetailed', {
          message: (err as Error).message
        }),
        'negative'
      );
      this.logger.error('Failed to generate section payload', err);
      return;
    }

    const metadata = stampGeneratedPayloadHash(baseMetadata, payload);
    const modifiedOn = new Date();
    const save$ = section?.Id
      ? this.saveConflictGuard(section.Id, this.loadedModifiedOn()).pipe(
          tap((confirmed) => {
            if (!confirmed) {
              this.isSaving.set(false);
            }
          }),
          filter(Boolean),
          switchMap(() =>
            this.metastore.update<{ Id: string; [key: string]: any }>(
              'UIDynamicView',
              {
                Id: section.Id,
                Payload: payload,
                Path: path,
                Metadata: JSON.stringify(metadata),
                ModifiedOn: modifiedOn
              }
            )
          )
        )
      : this.metastore.insert<{ Id: string; [key: string]: any }>(
          'UIDynamicView',
          {
            Payload: payload,
            Path: path,
            Type: 'section',
            Metadata: JSON.stringify(metadata),
            CreatedOn: new Date(),
            ModifiedOn: new Date()
          }
        );

    save$.subscribe({
      next: (saved) => {
        this.isSaving.set(false);
        this.loadedState.set({
          label,
          path,
          entityName: entity.name,
          columns: metadata.columns ?? [],
          linkedCardPath: metadata.linkedCardPath ?? null,
          allowCreate: metadata.allowCreate ?? false,
          allowDelete: this.allowDelete(),
          topActions: this.topActions(),
          topBar: metadata.topBar,
          config: metadata.config,
          tableSize: this.tableSize(),
          showManualFilter: this.showManualFilter(),
          showFilterPresets: this.showFilterPresets(),
          importLegacyFolders: metadata.importLegacyFolders ?? false
        });
        if (section?.Id) {
          this.loadedModifiedOn.set(
            saved['ModifiedOn'] ?? modifiedOn.toISOString()
          );
        }
        this.showTextAlert('dynamicEditor.saveSuccess', 'positive');
        this.tenantNavigate(['dynamic-views', 'section', saved.Id]);
      },
      error: (err) => {
        this.isSaving.set(false);
        this.showTextAlert('dynamicEditor.saveError', 'negative');
        this.logger.error('Failed to save UIDynamicView', err);
      }
    });
  }

  saveAsCopy() {
    const section = this.resolvedSection();
    if (!section) {
      return;
    }
    const entity = this.entity();
    if (!this.isPlatformView() && !entity) {
      this.showTextAlert(
        this.translate.instant('dynamicEditor.fillRequiredFields'),
        'negative'
      );
      return;
    }
    this.saveAsCopyFlow<SectionEditorMetadata>({
      type: 'section',
      source: section,
      rawSource: this.isPlatformView(),
      path: this.path(),
      label: this.label(),
      requireId: false,
      buildMetadata: (path, label) =>
        entity && !this.isPlatformView()
          ? this.buildMetadata(entity.name, path, label)
          : {
              entityName: this.sectionMetadata().entityName ?? '',
              label,
              path
            },
      buildPayload: (metadata) => this.generatePayload(metadata)
    }).subscribe({
      next: (saved) => {
        this.showTextAlert(
          this.translate.instant('dynamicEditor.copySaved'),
          'positive'
        );
        this.tenantNavigate(['dynamic-views', 'section', saved.Id]);
      },
      error: (err) => {
        this.showTextAlert(
          this.translate.instant('dynamicEditor.copySaveError'),
          'negative'
        );
        this.logger.error('Failed to save UIDynamicView copy', err);
      }
    });
  }
}
